import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeft, 
  Plus, 
  Search, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Eye,
  Calendar,
  MessageSquare
} from 'lucide-react';
import { getUserComplaints, getAllComplaints, Complaint, updateComplaintStatus } from '../../services/complaintsApi';
import { useRbac } from '../../context';


interface PaginationState {
  page: number;
  limit: number;
  totalPages: number;
  totalResults: number;
}

export function UserComplaints() {
  const navigate = useNavigate();
  const { hasPermission } = useRbac();
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 10,
    totalPages: 0,
    totalResults: 0,
  });

  const isAdmin = hasPermission('manageComplaints');

  const fetchComplaints = useCallback(async () => {
    setLoading(true);
    try {
      const response = isAdmin 
        ? await getAllComplaints(
            pagination.page, 
            pagination.limit, 
            selectedStatus === 'all' ? undefined : selectedStatus,
            selectedPriority === 'all' ? undefined : selectedPriority,
            selectedCategory === 'all' ? undefined : selectedCategory,
            searchTerm || undefined
          )
        : await getUserComplaints(pagination.page, pagination.limit, searchTerm);

      if (response.success) {
        setComplaints(response.data.complaints);
        setPagination(prev => ({
          ...prev,
          totalPages: response.data.pagination.totalPages,
          totalResults: response.data.pagination.totalResults,
        }));
      }
    } catch (error: any) {
      console.error('Error fetching complaints:', error);
      toast.error(error.message || 'Failed to fetch complaints');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, selectedStatus, selectedPriority, selectedCategory, isAdmin]);

  useEffect(() => {
    fetchComplaints();
  }, [fetchComplaints]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleStatusUpdate = async (complaintId: string, newStatus: string) => {
    try {
      await updateComplaintStatus(complaintId, newStatus);
      toast.success('Complaint status updated successfully');
      fetchComplaints(); // Refresh the list
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast.error(error.message || 'Failed to update complaint status');
    }
  };

  const handleFilterChange = (filterType: string, value: string) => {
    if (filterType === 'status') setSelectedStatus(value);
    if (filterType === 'priority') setSelectedPriority(value);
    if (filterType === 'category') setSelectedCategory(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-blue-100 text-blue-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'urgent':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <Clock className="w-4 h-4" />;
      case 'in_progress':
        return <AlertCircle className="w-4 h-4" />;
      case 'resolved':
        return <CheckCircle className="w-4 h-4" />;
      case 'closed':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
           
              <h1 className="text-2xl font-semibold text-gray-900">
                {isAdmin ? 'All Complaints' : 'My Complaints'}
              </h1>
            </div>
            {!isAdmin && (
              <Button
                onClick={() => navigate('/complaints/new')}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Complaint
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder={isAdmin ? "Search all complaints..." : "Search complaints..."}
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {isAdmin && (
              <div className="flex gap-2">
                <Select value={selectedStatus} onValueChange={(value) => handleFilterChange('status', value)}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={selectedPriority} onValueChange={(value) => handleFilterChange('priority', value)}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priority</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={selectedCategory} onValueChange={(value) => handleFilterChange('category', value)}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="Technical Issue">Technical Issue</SelectItem>
                    <SelectItem value="Account Problem">Account Problem</SelectItem>
                    <SelectItem value="Payment Issue">Payment Issue</SelectItem>
                    <SelectItem value="General Inquiry">General Inquiry</SelectItem>
                    <SelectItem value="Bug Report">Bug Report</SelectItem>
                    <SelectItem value="Feature Request">Feature Request</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Complaints List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
          </div>
        ) : complaints.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No complaints found</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm ? 'No complaints match your search criteria.' : 'You haven\'t submitted any complaints yet.'}
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => navigate('/complaints/new')}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Submit Your First Complaint
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {complaints.map((complaint) => (
              <Card key={complaint._id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <CardTitle className="text-lg">{complaint.title}</CardTitle>
                        <Badge className={getStatusColor(complaint.status)}>
                          {getStatusIcon(complaint.status)}
                          <span className="ml-1 capitalize">{complaint.status.replace('_', ' ')}</span>
                        </Badge>
                        <Badge className={getPriorityColor(complaint.priority)}>
                          {complaint.priority.toUpperCase()}
                        </Badge>
                      </div>
                      <CardDescription className="text-sm text-gray-600">
                        {complaint.description.length > 150 
                          ? `${complaint.description.substring(0, 150)}...` 
                          : complaint.description}
                      </CardDescription>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      {isAdmin && (
                        <Select 
                          value={complaint.status} 
                          onValueChange={(value) => handleStatusUpdate(complaint._id, value)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      <Button
                        variant={isAdmin ? "default" : "outline"}
                        size="sm"
                        onClick={() => navigate(isAdmin ? `/admin/complaints/${complaint._id}` : `/complaints/${complaint._id}`)}
                        className={isAdmin ? "bg-orange-600 hover:bg-orange-700" : ""}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {isAdmin ? "View Details" : "View"}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        Created: {formatDate(complaint.createdAt)}
                      </div>
                      {isAdmin && complaint.raisedBy && (
                        <div className="flex items-center">
                          <MessageSquare className="w-4 h-4 mr-1" />
                          By: {complaint.raisedBy.name || complaint.raisedBy.email}
                        </div>
                      )}
                      <div>
                        Category: {complaint.category}
                      </div>
                      <div>
                        Ticket ID: {complaint.ticketId}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {complaint.screenshots.length > 0 && (
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {complaint.screenshots.length} screenshot{complaint.screenshots.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      {complaint.attachments.length > 0 && (
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {complaint.attachments.length} file{complaint.attachments.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  {complaint.resolution && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-800">
                        <strong>Resolution:</strong> {complaint.resolution}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-8">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.totalResults)} of{' '}
              {pagination.totalResults} complaints
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Previous
              </Button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <Button
                      key={pageNum}
                      variant={pagination.page === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(pageNum)}
                      className="w-8 h-8 p-0"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
